import "../../../assets/scss/template-card-style.scss";
import { useEffect, useState, useRef, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { TemplateCardInterface } from "../../utility/interface/template-card";
import { Input, Skeleton, Button } from "antd";
import { TemplateCard } from "../../components/templateCard";
import { getTemplates } from "../../server/Templates";
import { useAppSelector } from "../../store/hooks";
import { useNavigate } from "react-router-dom";
import InfiniteCarousel from "../../components/InfiniteCarousel";

// GPU type AMD = 0, NVIDIA = 1
export const Templates = () => {
  const { t } = useTranslation();
  const selectionState = useAppSelector((state: any) => state.selection);
  const [filterValue, setFilterValue] = useState<string>("");
  const [searchValue, setSearchValue] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [templatesList, setTemplatesList] = useState<TemplateCardInterface[]>(
    []
  );
  const [filteredTemplatesList, setFilteredTemplatesList] = useState<
    TemplateCardInterface[]
  >([]);

  const navigate = useNavigate();
  const categoryRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // fixed filter options (all lowercase)
  const filterOptions = ["gai_app", "llm", "ide", "os"];

  const ENABLED_TEMPLATE_VERSIONS: Record<string, string[]> = {
    "anything-llm": [
      "phi3-14b",
      "phi3-3.8b",
      "stablelm2-12b",
      "starcoder2-7b",
      "deepseek-r1-32b",
    ],
    "gpu-jupyter": [
      "22.04-cuda12.2",
      "22.04-cuda11.8",
    ],
    "nv-cuda": [
      "22.04-cuda12.4.1-devel",
      "20.04-cuda12.4.1-devel",
      "22.04-cuda12.4.1-runtime",
      "20.04-cuda12.4.1-runtime",
      "22.04-cuda11.8-rc1.1",
    ],
    "open-webui": [
      "deepseek-r1-32b",
      "deepseek-r1-7b",
    ],
    "stable-diffusion": [
      "base",
    ],
    "tensorflow": [
      "22.04-cuda12.3-tf2.17",
    ],
    "vs-code": [
      "20.04-cuda11.6",
    ],
    "whisper": [
      "v1",
    ],
  };

  const normalizeName = (name: string): string => {
    return name.toLowerCase().replace(/_/g, "-");
  };

  const isVersionEnabled = (
    imageName?: string,
    imageVersion?: string,
    rawEnabled?: boolean
  ): boolean => {
    if (rawEnabled === false) return false;
    if (!imageName || !imageVersion) return false;
    const key = normalizeName(imageName);
    const versions = ENABLED_TEMPLATE_VERSIONS[key];
    if (versions) {
      return versions.includes(imageVersion);
    }
    return rawEnabled === true;
  };

  useEffect(() => {
    setLoading(true);
    fetchTemplateLst();
    setLoading(false);
  }, []);

  const fetchTemplateLst = async () => {
    try {
      const response = await getTemplates();
      const data = response.data.data;

      setTemplatesList(data);
      setFilteredTemplatesList(data);
    } catch (error) {
      console.error(error);
    }
  };

  const onChangeInput = (e: any) => {
    const value = e.target.value.toLowerCase();
    setSearchValue(value);

    applyFilters(filterValue, value);
  };

  const onFilterByLabel = (label: string) => {
    const newFilter = filterValue === label ? "" : label;
    setFilterValue(newFilter);
    applyFilters(newFilter, searchValue);
  };

  const applyFilters = (label: string, search: string) => {
    let filtered = templatesList;

    if (label) {
      filtered = filtered.filter(
        (template) => template.label?.toLowerCase() === label
      );
    }

    if (search) {
      filtered = filtered.filter((template) =>
        template.name.toLowerCase().includes(search)
      );
    }

    setFilteredTemplatesList(filtered);
  };

  const complexSlides: ReactNode[] = [
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        borderRadius: "16px",
      }}
    >
      <div className="slide-container" key="trellis">
        <h3 className="slide-title">TRELLIS: v1</h3>
        <p className="slide-descript">
          A 3D generative model that turns text or images into high-quality 3D
          assets.
        </p>
      </div>
    </div>,
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        borderRadius: "16px",
      }}
    >
      <div className="slide-container" key="deepseek">
        <h3 className="slide-title">DeepSeek-R1</h3>
        <p className="slide-descript">A reasoning-focused generative model.</p>
      </div>
    </div>,
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        borderRadius: "16px",
      }}
    >
      <div className="slide-container" key="dalle">
        <h3 className="slide-title">DALL-E</h3>
        <p className="slide-descript">
          AI image generator for creative assets.
        </p>
      </div>
    </div>,
  ];

  // Filter out P1-Quant, keep only enabled versions, and remove template groups with no enabled versions
  const activeTemplates = filteredTemplatesList
    .filter((item) => {
      const isP1Quant =
        item.name?.toLowerCase().includes("p1-quant") ||
        item.name?.toLowerCase().includes("p1quant") ||
        item.versions?.some(
          (v: any) =>
            v.id === 48 ||
            v.image_name?.toLowerCase().includes("p1-quant") ||
            v.image_name?.toLowerCase().includes("p1quant")
        );
      return !isP1Quant;
    })
    .map((item) => {
      const enabledVersions = (item.versions || [])
        .map((v: any) => ({
          ...v,
          enabled: isVersionEnabled(
            v.image_name || item.name,
            v.image_version,
            v.enabled
          ),
        }))
        .filter((v: any) => v.enabled);

      return {
        ...item,
        versions: enabledVersions,
      };
    })
    .filter((item) => item.versions.length > 0);

  // Group remaining templates by label for display
  const groupedTemplates = activeTemplates.reduce((acc: any, item) => {
    const label = item.label || "others";
    if (!acc[label]) acc[label] = [];
    acc[label].push(item);
    return acc;
  }, {});

  return (
    <div className="templates">
      <h3>{t("Template Selection")}</h3>

      {/* Carousel */}
      <section>
        <InfiniteCarousel slides={complexSlides} animationDuration={400} />
      </section>

      {/* Search + Filters */}
      <div className="template-sorting">
        <Input
          placeholder={t("Search templates...")}
          size="large"
          value={searchValue}
          onChange={onChangeInput}
          style={{ flex: 1 }}
        />

        <div className="filter-options">
          {filterOptions.map((label) => (
            <Button
              data-no-auth
              key={label}
              type={filterValue === label ? "primary" : "default"}
              onClick={() => onFilterByLabel(label)}
            >
              <img
                className={
                  filterValue === label ? "filter-icon active" : "filter-icon"
                }
                src={`/images/icons/${label}.svg`}
              />
              {label.toUpperCase()}
            </Button>
          ))}
        </div>
      </div>

      {loading && <Skeleton active />}

      <div className="template-card-container">
        {Object.keys(groupedTemplates).map((label) => (
          <div key={label} className="template-category-section">
            <h2 className="category-heading">{label.toUpperCase()}</h2>
            <div className="category-templates">
              {groupedTemplates[label].map((item: any) => {
                const primaryVersion = item.versions[0] || {};
                return (
                  <TemplateCard
                    key={primaryVersion.id || item.name}
                    template_id={primaryVersion.id}
                    label={item.label}
                    template_name={`${item.name}: ${
                      primaryVersion.image_version || "latest"
                    }`}
                    description={primaryVersion.description}
                    icon_url={item.icon_url}
                    start_script={item.cli}
                    vendor={item.vendor}
                    name={item.name}
                    versions={item.versions}
                    enabled={true}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
